import React, { ReactNode, useRef, useState, useEffect } from "react";
import {
  CheckPicker,
  Checkbox,
  Button as RsuiteButton,
  DatePicker,
} from "rsuite";
import type { PickerHandle } from "rsuite/esm/internals/Picker";
import moment from "moment-timezone";
import { UserSegmentEnum } from "../../../types";
import { getSegmentLabel } from "../../../utils/segments";
import Button from "../../../componentLibrary/Button";

interface AnalyticsFiltersProps {
  selectedSegments: UserSegmentEnum[];
  onSegmentsChange: (segments: UserSegmentEnum[]) => void;
  startDate?: string;
  endDate?: string;
  onStartDateChange: (date?: string) => void;
  onEndDateChange: (date?: string) => void;
  mobileMenuButton?: ReactNode;
  mobileFilterButton?: ReactNode;
  renderContentOnly?: boolean;
  onCustomTimeframeChange?: (isCustom: boolean) => void;
}

type TimeframeOption =
  | "today"
  | "7d"
  | "14d"
  | "30d"
  | "90d"
  | "all"
  | "custom";

const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = ({
  selectedSegments,
  onSegmentsChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  mobileMenuButton,
  mobileFilterButton,
  renderContentOnly = false,
  onCustomTimeframeChange,
}) => {
  const pickerRef = useRef<PickerHandle | null>(null);
  const shouldKeepSegmentPickerOpenRef = useRef(false);
  const [selectedTimeframe, setSelectedTimeframe] =
    useState<TimeframeOption>("all");
  const [isSegmentPickerOpen, setIsSegmentPickerOpen] = useState(false);

  // Generate options dynamically from all enum values
  const segmentOptions = Object.values(UserSegmentEnum).map((value) => ({
    label: getSegmentLabel(value as UserSegmentEnum),
    value: value as UserSegmentEnum,
  }));

  // All possible values for "check all" functionality
  const allValues = segmentOptions.map((item) => item.value);

  // Handle CheckPicker change
  const handleSegmentChange = (value: string[]) => {
    shouldKeepSegmentPickerOpenRef.current = true;
    onSegmentsChange(value as UserSegmentEnum[]);
  };

  const handleSegmentSelect = () => {
    shouldKeepSegmentPickerOpenRef.current = true;
    setIsSegmentPickerOpen(true);
  };

  const handleSegmentPickerClose = () => {
    // Rsuite may invoke onClose before onChange/onSelect in the same click; defer so
    // handleSegmentChange / handleCheckAll can set shouldKeepSegmentPickerOpenRef first.
    queueMicrotask(() => {
      if (shouldKeepSegmentPickerOpenRef.current) {
        shouldKeepSegmentPickerOpenRef.current = false;
        setIsSegmentPickerOpen(true);
        return;
      }

      setIsSegmentPickerOpen(false);
    });
  };

  const closeSegmentPicker = () => {
    shouldKeepSegmentPickerOpenRef.current = false;
    pickerRef.current?.close();
    setIsSegmentPickerOpen(false);
  };

  // Handle "Check All" checkbox change
  const handleCheckAll = (_value: string, checked: boolean) => {
    shouldKeepSegmentPickerOpenRef.current = true;
    onSegmentsChange(checked ? allValues : []);
  };

  // Custom rendering of selected values
  const renderValue = (values: UserSegmentEnum[]) => {
    // If all values are selected, show "All Segments"
    if (values.length === allValues.length) {
      return "All Segments";
    }

    // If no values selected
    if (values.length === 0) {
      return "Select segments...";
    }

    // Show truncated list for better UX - show first 2 items + count
    const maxVisibleItems = 2;
    const visibleItems = values.slice(0, maxVisibleItems).map((value) => {
      const option = segmentOptions.find((item) => item.value === value);
      return option ? option.label : value;
    });

    const remainingCount = values.length - maxVisibleItems;

    if (remainingCount > 0) {
      return `${visibleItems.join(", ")} +${remainingCount} more`;
    }

    return visibleItems.join(", ");
  };

  // Custom footer styles
  const footerStyles = {
    padding: "10px 12px",
    borderTop: "1px solid #e5e5e5",
  };

  const footerButtonStyle = {
    float: "right" as const,
    marginRight: 10,
    marginTop: 2,
  };

  // Handle start date change
  const handleStartDateChange = (value: Date | null) => {
    if (!value) {
      onStartDateChange(undefined);
      return;
    }

    // Convert local date to UTC at start of day (00:00:00) in local timezone
    const startOfDayLocal = moment(value).startOf("day");
    onStartDateChange(startOfDayLocal.toISOString());

    // Don't auto-fill end date - allow one-sided interval (start to infinity)
  };

  // Handle end date change
  const handleEndDateChange = (value: Date | null) => {
    if (!value) {
      onEndDateChange(undefined);
      return;
    }

    // Convert local date to UTC at end of day (23:59:59) in local timezone
    const endOfDayLocal = moment(value).endOf("day");
    onEndDateChange(endOfDayLocal.toISOString());

    // Don't auto-fill start date - allow one-sided interval (beginning to end)
  };

  // Handle timeframe button click
  const handleTimeframeChange = (timeframe: TimeframeOption) => {
    setSelectedTimeframe(timeframe);

    if (timeframe === "all") {
      onStartDateChange(undefined);
      onEndDateChange(undefined);
      return;
    }

    if (timeframe === "custom") {
      return;
    }

    const end = moment();
    const start = moment();

    switch (timeframe) {
      case "today":
        break;
      case "7d":
        start.subtract(7, "days");
        break;
      case "14d":
        start.subtract(14, "days");
        break;
      case "30d":
        start.subtract(30, "days");
        break;
      case "90d":
        start.subtract(90, "days");
        break;
    }

    onStartDateChange(start.startOf("day").toISOString());
    onEndDateChange(end.endOf("day").toISOString());
  };

  useEffect(() => {
    if (!startDate && !endDate) {
      setSelectedTimeframe("all");
    } else if (startDate || endDate) {
      const start = startDate ? moment(startDate) : null;
      const end = endDate ? moment(endDate) : null;

      if (start && end) {
        const daysDiff = end.diff(start, "days");
        if (daysDiff === 0) {
          setSelectedTimeframe("today");
        } else if (daysDiff >= 6 && daysDiff <= 8) {
          setSelectedTimeframe("7d");
        } else if (daysDiff >= 13 && daysDiff <= 15) {
          setSelectedTimeframe("14d");
        } else if (daysDiff >= 29 && daysDiff <= 31) {
          setSelectedTimeframe("30d");
        } else if (daysDiff >= 89 && daysDiff <= 91) {
          setSelectedTimeframe("90d");
        } else {
          setSelectedTimeframe("custom");
        }
      } else {
        setSelectedTimeframe("custom");
      }
    }
  }, [startDate, endDate]);

  useEffect(() => {
    onCustomTimeframeChange?.(selectedTimeframe === "custom");
  }, [selectedTimeframe, onCustomTimeframeChange]);

  useEffect(() => {
    if (!isSegmentPickerOpen) {
      return;
    }

    const initialScrollX = window.scrollX;
    const initialScrollY = window.scrollY;

    const handleScroll = () => {
      if (
        window.scrollX === initialScrollX &&
        window.scrollY === initialScrollY
      ) {
        return;
      }

      closeSegmentPicker();
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isSegmentPickerOpen]);

  const timeframeButtons: { value: TimeframeOption; label: string }[] = [
    { value: "today", label: "Today" },
    { value: "7d", label: "7d" },
    { value: "14d", label: "14d" },
    { value: "30d", label: "30d" },
    { value: "90d", label: "90d" },
    { value: "all", label: "All-time" },
    { value: "custom", label: "Custom" },
  ];

  // Filter content that will be reused in mobile dropdown
  const filterContent = (
    <div className="space-y-4">
      {/* Segment Selector */}
      <div className="flex flex-col space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Select Segments:
        </label>
        <CheckPicker
          data={segmentOptions}
          value={selectedSegments}
          open={isSegmentPickerOpen}
          onChange={handleSegmentChange}
          onSelect={handleSegmentSelect}
          onOpen={() => setIsSegmentPickerOpen(true)}
          onClose={handleSegmentPickerClose}
          placeholder="Select segments..."
          cleanable={false}
          ref={pickerRef}
          renderValue={renderValue}
          className="w-full"
          style={{ zIndex: 9999 }}
          menuStyle={{ zIndex: 9999 }}
          renderExtraFooter={() => (
            <div style={footerStyles}>
              <Checkbox
                indeterminate={
                  selectedSegments.length > 0 &&
                  selectedSegments.length < allValues.length
                }
                checked={selectedSegments.length === allValues.length}
                onChange={handleCheckAll}
              >
                Check all
              </Checkbox>

              <RsuiteButton
                style={footerButtonStyle}
                appearance="primary"
                size="sm"
                onClick={() => {
                  closeSegmentPicker();
                }}
              >
                Ok
              </RsuiteButton>
            </div>
          )}
        />
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {timeframeButtons.map(({ value, label }) => (
          <Button
            key={value}
            onClick={() => handleTimeframeChange(value)}
            variant={selectedTimeframe === value ? "dark" : "secondary"}
            size="sm"
          >
            {label}
          </Button>
        ))}
      </div>

      {selectedTimeframe === "custom" && (
        <>
          {/* Date Range Selector */}
          <div className="flex flex-col space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Start Date:
            </label>
            <DatePicker
              value={startDate ? moment(startDate).toDate() : null}
              onChange={handleStartDateChange}
              format="MMM dd, yyyy"
              cleanable={true}
              placeholder="From..."
              placement="bottomStart"
              className="w-full"
              style={{ zIndex: 9998 }}
              menuStyle={{ zIndex: 9998 }}
              shouldDisableDate={(date) => {
                // Disable future dates
                if (date > new Date()) return true;
                // Disable dates after end date if end date is set
                if (endDate && date > moment(endDate).toDate()) return true;
                return false;
              }}
            />
          </div>

          {/* End Date Selector */}
          <div className="flex flex-col space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              End Date:
            </label>
            <DatePicker
              value={endDate ? moment(endDate).toDate() : null}
              onChange={handleEndDateChange}
              format="MMM dd, yyyy"
              cleanable={true}
              placeholder="To..."
              placement="bottomStart"
              className="w-full"
              style={{ zIndex: 9997 }}
              menuStyle={{ zIndex: 9997 }}
              shouldDisableDate={(date) => {
                // Disable future dates
                if (date > new Date()) return true;
                // Disable dates before start date if start date is set
                if (startDate && date < moment(startDate).toDate()) return true;
                return false;
              }}
            />
          </div>
        </>
      )}
    </div>
  );

  // If renderContentOnly is true, just return the filter content
  if (renderContentOnly) {
    return filterContent;
  }

  return (
    <div className="fixed top-12 left-0 right-0 z-50 bg-white px-4 py-4 shadow-md">
      {/* Desktop View - Horizontal Layout (lg and above) */}
      <div className="hidden lg:block">
        {/* First Row: Segment Selector and Timeframe Buttons */}
        <div className="flex gap-4 items-center">
          {/* Segment Selector - First on the left */}
          <div className="flex space-x-4 items-center">
            <label className="block mb-0">Select Segments:</label>
            <CheckPicker
              data={segmentOptions}
              value={selectedSegments}
              open={isSegmentPickerOpen}
              onChange={handleSegmentChange}
              onSelect={handleSegmentSelect}
              onOpen={() => setIsSegmentPickerOpen(true)}
              onClose={handleSegmentPickerClose}
              placeholder="Select segments..."
              cleanable={false}
              ref={pickerRef}
              renderValue={renderValue}
              menuStyle={{ zIndex: 9999 }}
              renderExtraFooter={() => (
                <div style={footerStyles}>
                  <Checkbox
                    indeterminate={
                      selectedSegments.length > 0 &&
                      selectedSegments.length < allValues.length
                    }
                    checked={selectedSegments.length === allValues.length}
                    onChange={handleCheckAll}
                  >
                    Check all
                  </Checkbox>

                  <RsuiteButton
                    style={footerButtonStyle}
                    appearance="primary"
                    size="sm"
                    onClick={() => {
                      closeSegmentPicker();
                    }}
                  >
                    Ok
                  </RsuiteButton>
                </div>
              )}
            />
          </div>

          {/* Timeframe Buttons */}
          <div className="flex gap-2">
            {timeframeButtons.map(({ value, label }) => (
              <Button
                key={value}
                onClick={() => handleTimeframeChange(value)}
                variant={selectedTimeframe === value ? "dark" : "secondary"}
                size="sm"
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        {/* Second Row: Date pickers - only show when Custom is selected */}
        {selectedTimeframe === "custom" && (
          <div className="flex gap-4 items-center mt-3">
            <div className="flex space-x-4 items-center">
              <label className="block mb-0">Start Date:</label>
              <DatePicker
                value={startDate ? moment(startDate).toDate() : null}
                onChange={handleStartDateChange}
                format="MMM dd, yyyy"
                cleanable={true}
                placeholder="From..."
                placement="bottomStart"
                menuStyle={{ zIndex: 9999 }}
                shouldDisableDate={(date) => {
                  if (date > new Date()) return true;
                  if (endDate && date > moment(endDate).toDate()) return true;
                  return false;
                }}
              />
            </div>

            <div className="flex space-x-4 items-center">
              <label className="block mb-0">End Date:</label>
              <DatePicker
                value={endDate ? moment(endDate).toDate() : null}
                onChange={handleEndDateChange}
                format="MMM dd, yyyy"
                cleanable={true}
                placeholder="To..."
                placement="bottomStart"
                menuStyle={{ zIndex: 9999 }}
                shouldDisableDate={(date) => {
                  if (date > new Date()) return true;
                  if (startDate && date < moment(startDate).toDate())
                    return true;
                  return false;
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile View - Button Layout (below lg) */}
      <div className="lg:hidden flex gap-2 items-start w-full">
        {/* Tab hamburger menu */}
        {mobileMenuButton && <div className="flex-1">{mobileMenuButton}</div>}

        {/* Filters button */}
        {mobileFilterButton && (
          <div className="flex-1">{mobileFilterButton}</div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsFilters;
